// SA7 — Auditoría cross-tenant (Super Admin). Eventos de TODOS los tenants,
// con columna Tenant + filtro por tenant. Solo lectura.

import { useState } from 'react';
import type { AuditoriaQuery } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SuperAdminSidebar } from '../../components/layout/SuperAdminSidebar';
import { useSuperAuditoria } from '../../lib/api/queries';

const TIPOS = [
  'create',
  'update',
  'soft_delete',
  'restore',
  'login',
  'logout',
  'suplantacion_inicio',
  'suplantacion_fin',
  'export',
  'import',
] as const;
type Filtros = AuditoriaQuery & { tenant_id?: string };

export function SA7AuditoriaPage() {
  const [filtros, setFiltros] = useState<Filtros>({ limit: 100, offset: 0 });
  const { data, isLoading, isError } = useSuperAuditoria(filtros);
  const pageActual = Math.floor(filtros.offset / filtros.limit) + 1;
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / filtros.limit)) : 1;

  return (
    <RoleLayout sidebar={<SuperAdminSidebar />} roleLabel="Super Admin · Jidoka">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <header>
          <h1 className="text-xl font-medium text-fg">Auditoría cross-tenant</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {data?.total ?? 0} evento(s) · todos los tenants
          </p>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <>
            <Card>
              <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3 xl:grid-cols-6">
                <div className="flex flex-col gap-1">
                  <Label>Tenant</Label>
                  <Select
                    value={filtros.tenant_id ?? ''}
                    onChange={(e) =>
                      setFiltros({ ...filtros, tenant_id: e.target.value || undefined, offset: 0 })
                    }
                  >
                    <option value="">Todos</option>
                    {data.recursos.tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nombre}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Desde</Label>
                  <Input
                    type="date"
                    value={filtros.desde ?? ''}
                    onChange={(e) =>
                      setFiltros({ ...filtros, desde: e.target.value || undefined, offset: 0 })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Hasta</Label>
                  <Input
                    type="date"
                    value={filtros.hasta ?? ''}
                    onChange={(e) =>
                      setFiltros({ ...filtros, hasta: e.target.value || undefined, offset: 0 })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Tipo</Label>
                  <Select
                    value={filtros.tipo_evento ?? ''}
                    onChange={(e) =>
                      setFiltros({
                        ...filtros,
                        tipo_evento: (e.target.value || undefined) as Filtros['tipo_evento'],
                        offset: 0,
                      })
                    }
                  >
                    <option value="">Todos</option>
                    {TIPOS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Entidad</Label>
                  <Input
                    value={filtros.entidad_tipo ?? ''}
                    onChange={(e) =>
                      setFiltros({
                        ...filtros,
                        entidad_tipo: e.target.value || undefined,
                        offset: 0,
                      })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Buscar razón</Label>
                  <Input
                    value={filtros.q ?? ''}
                    onChange={(e) =>
                      setFiltros({ ...filtros, q: e.target.value || undefined, offset: 0 })
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {data.eventos.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-fg-tertiary">
                  Sin eventos con esos filtros.
                </CardContent>
              </Card>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Fecha</TH>
                    <TH>Tenant</TH>
                    <TH>Actor</TH>
                    <TH align="center">Tipo</TH>
                    <TH>Entidad</TH>
                    <TH>Razón</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.eventos.map((e) => (
                    <TR key={e.id}>
                      <TD className="font-mono text-xs text-fg-secondary">
                        {new Date(e.created_at).toLocaleString('es-CO', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TD>
                      <TD className="text-xs font-medium text-fg">{e.tenant_nombre ?? '—'}</TD>
                      <TD className="text-xs text-fg-secondary">
                        {e.actor_nombre
                          ? `${e.actor_nombre} ${e.actor_apellido ?? ''}`
                          : (e.actor_email ?? '—')}
                      </TD>
                      <TD align="center">
                        <Badge
                          tone={
                            e.tipo_evento === 'create'
                              ? 'ok'
                              : e.tipo_evento.includes('delete')
                                ? 'crit'
                                : 'warn'
                          }
                        >
                          {e.tipo_evento}
                        </Badge>
                      </TD>
                      <TD className="text-xs text-fg-secondary">{e.entidad_tipo}</TD>
                      <TD className="max-w-xs truncate text-xs text-fg">{e.razon ?? '—'}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}

            {totalPaginas > 1 && (
              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="text-xs text-fg-tertiary">
                  Página {pageActual} de {totalPaginas}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={filtros.offset === 0}
                    onClick={() =>
                      setFiltros({
                        ...filtros,
                        offset: Math.max(0, filtros.offset - filtros.limit),
                      })
                    }
                  >
                    ← Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={pageActual >= totalPaginas}
                    onClick={() =>
                      setFiltros({ ...filtros, offset: filtros.offset + filtros.limit })
                    }
                  >
                    Siguiente →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </RoleLayout>
  );
}
